import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";
import type { TenantPlanOption } from "@/services/authService";

/**
 * Compact, accordion-style plan picker for the accountant client-onboarding
 * plan step. Each plan is a small collapsed row (name + price); the SELECTED
 * plan expands to reveal its tagline + feature bullets, so the list stays short
 * (one expanded at a time) instead of the tall always-expanded onboarding grid.
 *
 * Free detection is PRICE-BASED (`price === 0`) — do NOT use `isFreePlan()`:
 * that helper is a premium-feature gate (literalE|tala|free|starter), and
 * `tala` is a billable $513/mo tier, so gating "Gratis" on it mislabels a paid
 * plan (and routes it through the free-activation path the backend rejects).
 *
 * Prices come from `tenantPrice*`, never `price*`: for an Art. 5 (Literal E /
 * IVA mínimo) tenant the server has already deducted the 80 UI credit, and the
 * catalog price is not what they will be charged. `priceMonthly` is still read,
 * but only to strike it through beside the discounted figure.
 */
const DEFAULT_FEATURED_PLAN_ID = "ombu";

function formatPrice(uyu: number): string {
  return uyu.toLocaleString("es-UY");
}

export interface PlanCardGridProps {
  plans: TenantPlanOption[];
  selectedPlanId: string | null;
  interval: "monthly" | "annual";
  onSelectPlan: (planId: string) => void;
  onIntervalChange: (interval: "monthly" | "annual") => void;
  /** Plan id to badge "Recomendado". Defaults to Ombú. */
  featuredPlanId?: string;
}

export function PlanCardGrid({
  plans,
  selectedPlanId,
  interval,
  onSelectPlan,
  onIntervalChange,
  featuredPlanId = DEFAULT_FEATURED_PLAN_ID,
}: PlanCardGridProps): JSX.Element {
  const { t } = useTranslation();
  // The 80 UI credit accrues monthly, so a tenant receiving it is billed monthly
  // only and the server returns tenantPriceAnnual = null for their billable
  // tiers. Hide the interval selector rather than offer a cycle the checkout
  // would reject.
  const annualOffered =
    plans.length > 0 && plans.every((p) => p.tenantPriceAnnual !== null);
  const annual = annualOffered && interval === "annual";

  useEffect(() => {
    if (!annualOffered && interval === "annual") {
      onIntervalChange("monthly");
    }
  }, [annualOffered, interval, onIntervalChange]);

  if (plans.length === 0) {
    return (
      <p className="fx-acct-auto-note">
        {t(
          "accountantClientOnboarding.plan.noPlans",
          "No hay planes disponibles en este momento.",
        )}
      </p>
    );
  }

  return (
    <div className="fx-acct-plan-picker">
      {annualOffered ? (
      <div
        className="fx-acct-plan-interval"
        role="tablist"
        aria-label={t(
          "accountantClientOnboarding.plan.intervalLabel",
          "Ciclo de facturación",
        )}
      >
        {(["monthly", "annual"] as const).map((iv) => (
          <button
            key={iv}
            type="button"
            role="tab"
            aria-selected={interval === iv}
            className={cn(
              "fx-acct-plan-interval-btn",
              interval === iv && "is-active",
            )}
            onClick={() => onIntervalChange(iv)}
          >
            {iv === "monthly"
              ? t("accountantClientOnboarding.plan.monthly", "Mensual")
              : t("accountantClientOnboarding.plan.annual", "Anual")}
          </button>
        ))}
      </div>
      ) : null}

      <div className="fx-acct-plan-accordion">
        {plans.map((p) => {
          const active = selectedPlanId === p.id;
          const price = annual
            ? (p.tenantPriceAnnual ?? p.priceAnnual)
            : p.tenantPriceMonthly;
          const listPrice = annual ? p.priceAnnual : p.priceMonthly;
          // Only meaningful monthly — the benefit is never applied annually.
          const discounted = !annual && p.fiscalBenefitDiscount > 0;
          const isFree = price === 0;
          const isFeatured = p.id === featuredPlanId;
          const tagline = t(`settings.plans.${p.id}.tagline`, "");
          const hasTagline =
            tagline !== "" && tagline !== `settings.plans.${p.id}.tagline`;
          const bullets = t(`settings.plans.${p.id}.descriptionBullets`, {
            returnObjects: true,
          });
          const features = Array.isArray(bullets)
            ? bullets.map((b) => String(b))
            : [];

          return (
            <div
              key={p.id}
              className={cn("fx-acct-plan-item", active && "is-active")}
            >
              <button
                type="button"
                role="radio"
                aria-checked={active}
                aria-expanded={active}
                className="fx-acct-plan-item-head"
                onClick={() => onSelectPlan(p.id)}
              >
                <span className="fx-acct-plan-item-name">
                  {t(p.nameKey, p.id.toUpperCase())}
                  {isFeatured ? (
                    <span className="fx-acct-plan-badge">
                      {t("onboarding.plansStep.recommended", "Recomendado")}
                    </span>
                  ) : null}
                </span>
                <span className="fx-acct-plan-item-price">
                  {isFree ? (
                    <span className="free">
                      {t("accountantClientOnboarding.plan.free", "Gratis")}
                    </span>
                  ) : (
                    <>
                      {discounted ? (
                        <s className="fx-acct-plan-item-was">
                          {formatPrice(listPrice)}
                        </s>
                      ) : null}
                      <span className="cur">UYU</span>
                      {formatPrice(price)}
                      <span className="per">
                        {annual
                          ? t("accountantClientOnboarding.plan.perYear", "/ año")
                          : t(
                              "accountantClientOnboarding.plan.perMonth",
                              "/ mes",
                            )}
                      </span>
                    </>
                  )}
                </span>
                <ChevronDown
                  className={cn(
                    "fx-acct-plan-item-chev",
                    active && "is-open",
                  )}
                  aria-hidden
                />
              </button>

              {active ? (
                <div className="fx-acct-plan-item-body">
                  {hasTagline ? (
                    <p className="fx-acct-plan-item-tagline">{tagline}</p>
                  ) : null}
                  {features.length > 0 ? (
                    <ul className="fx-acct-plan-item-features">
                      {features.map((feature, i) => (
                        <li key={i}>
                          <Check aria-hidden />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {discounted ? (
                    <p className="fx-acct-plan-item-benefit">
                      {t("settings.plans.fiscalBenefitApplied", {
                        defaultValue:
                          "Incluye el crédito fiscal de {{amount}} UYU/mes (80 UI, Dec. 206/019).",
                        amount: formatPrice(p.fiscalBenefitDiscount),
                      })}
                    </p>
                  ) : null}
                  {!isFree ? (
                    <p className="fx-acct-plan-item-iva">
                      {t("settings.plans.plusIva", "+ IVA")}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
